import * as Sentry from '@sentry/browser';

import { reportExceptionIntegration } from './reportExceptionIntegration';
import type { SentryConfig } from './sentry';

Sentry.addTracingExtensions();

/**
 * Initialize Sentry in this runtime.
 */
export function initializeSentry(config: SentryConfig) {
    Sentry.init({
        dsn: config.dsn,
        environment: config.projectId,
        release: config.release,
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 0.01,
        ignoreErrors: [
            // Network errors such as going offline or being blocked by a proxy
            'Failed to fetch',
            'Network Error',
            'CustomEvent',
            // Filter out annoying CefSharp issues
            // https://forum.sentry.io/t/unhandledrejection-non-error-promise-rejection-captured-with-value/14062/20
            'Non-Error promise rejection captured with value: Object Not Found Matching Id',
            /**
             * This error can be triggered by browsers during virtualization.
             * It means that ResizeObserver was not able to deliver all observations within a single animation frame.
             * It is benign (your site will not break).
             * Source - https://stackoverflow.com/questions/49384120/resizeobserver-loop-limit-exceeded
             */
            /^ResizeObserver loop limit exceeded$/,
        ],
        beforeSend: (event, hint) => {
            const error = hint.originalException;
            // Grouping FirebaseErrors that are thrown when the "client is offline"
            // under the same sentry event fingerprint which can be thrown from
            // several places in the codebase with different stack traces.
            if (
                error instanceof Error &&
                error?.name === 'FirebaseError' &&
                error?.message?.match(/Failed to get document because the client is offline/i)
            ) {
                event.fingerprint = ['client-offline'];
            }

            return event;
        },
        integrations: (defaultIntegrations) =>
            defaultIntegrations
                .map((integration) => {
                    if (integration.name === 'Breadcrumbs') {
                        // Console logging must be disabled since
                        // it's very slow to serialize some JS objects
                        return Sentry.breadcrumbsIntegration({
                            console: false,
                        });
                    }

                    return integration;
                })
                .concat([
                    Sentry.browserTracingIntegration({
                        // We don't use sentry-trace header for now
                        // and it doens't work with some external services like castle.io with strict CORS policy
                        tracingOrigins: [],
                        traceFetch: false,
                        traceXHR: false,
                    }),
                    reportExceptionIntegration(),
                ])
                .concat(
                    config.recordReplays
                        ? [
                              Sentry.replayIntegration({
                                  maskAllText: true,
                                  // Try to use the experimental features to capture more data
                                  _experiments: {
                                      captureExceptions: true,
                                      traceInternals: true,
                                  },
                              }),
                          ]
                        : []
                ),
        // We don't track error when developing
        enabled: window.location.hostname !== '127.0.0.1' && !process.env.GITBOOK_NO_ANALYTICS,
        tracesSampler: ({ sampleRate }) => {
            const configRate =
                typeof config.tracesSampling === 'number' ? config.tracesSampling : 1;
            // Use the transaction custom rate if explicitely set
            return typeof sampleRate === 'number' ? Number(sampleRate) : configRate;
        },
    });
}

/**
 * Configure Sentry user / etc
 */
export const getCurrentScope = Sentry.getCurrentScope;

/**
 * Flush all events to Sentry.
 */
export async function flushToSentry(): Promise<void> {
    await Sentry.flush(2000);
}
